///
/// EarthBrowser
///
/// Created by Matt Giger on 12/4/2025
/// Copyright (c) 2025 Geodata Labs
///

import type { Camera } from "@stovokor/camera/Camera"
import { type UIViewportInterface } from "@stovokor/engine/UIViewport"
import { ViewportInterface } from "@stovokor/engine/WorldViewport"
import type { StovokorContext } from "@stovokor/StovokorContext"
import { FocusTarget, type FocusChangeCallback, type FocusSettledCallback, type FocusTransitionConfig } from "./FocusTarget"
import { FocusTransition, type FocusTransitionParams } from "./FocusTransition"

/**
 * FocusManager - Manages focus state and transitions between gesture nodes
 *
 * Key concepts:
 * - **Focus**: One node has "focus" at a time, receiving zoom events
 * - **Default Focus**: Fallback node when explicit focus is released (e.g., Earth)
 * - **UI Viewport Handler**: First-priority hit testing for 2D UI elements
 * - **Viewport Handler**: Last-resort fallback when no default focus (3D world)
 * - **Transitions**: Animated camera movement when focus changes
 *
 * Focus hierarchy (highest to lowest priority):
 * 1. Explicitly focused node (via requestFocus)
 * 2. Default focus node (e.g., Earth)
 * 3. Viewport handler (scene root)
 *
 * Hit test priority (for gesture recognition):
 * 1. UI viewport (2D panes) - checked first
 * 2. World viewport (3D scene) - checked if UI doesn't consume
 */
export class FocusManager {
	private context: StovokorContext
	private camera!: Camera
	private focusedNode?: FocusTarget
	private defaultFocus?: FocusTarget
	private labelViewportHandler?: UIViewportInterface
	private uiViewportHandler?: UIViewportInterface
	private viewportHandler?: FocusTarget & ViewportInterface
	private focusChangeCallbacks = new Set<FocusChangeCallback>()
	private focusSettledCallbacks = new Set<FocusSettledCallback>()
	private focusTransition?: FocusTransition

	constructor(context: StovokorContext) {
		this.context = context
	}

	// ========== Configuration ==========

	/**
	 * Set the viewport handler (last-resort fallback)
	 */
	public setViewportHandler(handler: FocusTarget & ViewportInterface): void {
		this.viewportHandler = handler
		this.camera = handler.camera
		this.focusTransition = new FocusTransition(handler.camera)

		// If no current focus and no default focus, activate viewport handler immediately
		if (!this.focusedNode && !this.defaultFocus && this.camera) {
			handler.onFocusGained?.(this.camera)
		}
	}

	/**
	 * Get the viewport handler
	 */
	public getViewportHandler(): (FocusTarget & ViewportInterface) | undefined {
		return this.viewportHandler
	}

	/**
	 * Set the label viewport handler (for depth-preserving label gesture hit testing)
	 *
	 * The label viewport renders between the world and UI passes,
	 * preserving the world depth buffer for occlusion testing.
	 */
	public setLabelViewportHandler(handler: UIViewportInterface): void {
		this.labelViewportHandler = handler
	}

	/**
	 * Get the label viewport handler
	 */
	public getLabelViewportHandler(): UIViewportInterface | undefined {
		return this.labelViewportHandler
	}

	/**
	 * Set the UI viewport handler (for 2D pane gesture hit testing)
	 *
	 * The UI viewport is checked first during gesture hit testing,
	 * allowing UI elements to intercept gestures before the 3D world.
	 */
	public setUIViewportHandler(handler: UIViewportInterface): void {
		this.uiViewportHandler = handler
	}

	/**
	 * Get the UI viewport handler
	 */
	public getUIViewportHandler(): UIViewportInterface | undefined {
		return this.uiViewportHandler
	}

	/**
	 * Set the default focus node
	 *
	 * The default focus is the node that receives focus when releaseFocus() is called,
	 * instead of falling back to the viewport handler. This is typically the Earth
	 * node, so clearing focus from a satellite returns control to Earth navigation.
	 */
	public setDefaultFocus(node: FocusTarget): void {
		this.defaultFocus = node

		// If no current focus and camera is set, activate default focus immediately
		if (!this.focusedNode && this.camera) {
			this.viewportHandler?.onFocusLost?.()
			node.onFocusGained?.(this.camera)
		}
	}

	/**
	 * Clear the default focus
	 */
	clearDefaultFocus(): void {
		const wasDefault = this.defaultFocus
		this.defaultFocus = undefined

		// If no focused node and we had a default, transfer to viewport
		if (!this.focusedNode && wasDefault && this.camera) {
			wasDefault.onFocusLost?.()
			this.viewportHandler?.onFocusGained?.(this.camera)
		}
	}

	/**
	 * Get the default focus node
	 */
	public getDefaultFocus(): FocusTarget | undefined {
		return this.defaultFocus
	}

	// ========== Focus State API ==========

	/**
	 * Get the active handler (focused node → default focus → viewport fallback)
	 */
	public getActiveHandler(): FocusTarget | undefined {
		return this.focusedNode ?? this.defaultFocus ?? this.viewportHandler
	}

	/**
	 * Check if a specific node is currently focused
	 */
	public isFocused(node: FocusTarget): boolean {
		return this.focusedNode?.id === node.id
	}

	/**
	 * Get the currently focused node (not including default focus or viewport)
	 */
	public getFocusedNode(): FocusTarget | undefined {
		return this.focusedNode
	}

	// ========== Focus Command API ==========

	/**
	 * Send a command to the active handler
	 * @param command The command name (e.g., "compass/reset", "zoom/in")
	 * @param data Optional command data
	 * @returns true if the command was handled
	 */
	public sendCommand(command: string, data?: unknown): boolean {
		const handler = this.getActiveHandler()
		if (!handler) {
			this.context.logger.warn(`[FocusManager] No handler for command: ${command}`)
			return false
		}

		const handled = handler.handleCommand?.(command, data) ?? false

		if (!handled) {
			this.context.logger.log(`[FocusManager] Command not handled: ${command}`)
		}

		return handled
	}

	// ========== Focus Management ==========

	/**
	 * Request focus for a node with optional camera transition
	 *
	 * @param requester The node requesting focus
	 * @param transition Optional transition config to override the node's default
	 *                   - If provided with target: uses the specified target and params
	 *                   - If provided without target: uses node's target with specified params
	 *                   - If not provided: uses node's focusTransition() entirely
	 * @returns true if focus was granted
	 */
	public requestFocus(requester: FocusTarget, transition?: FocusTransitionConfig | FocusTransitionParams): boolean {
		if (!requester.focusable) {
			return false
		}

		if (!this.camera) {
			this.context.logger.warn("[FocusManager] Cannot focus - camera not set")
			return false
		}

		// Skip if already focused on this node
		if (this.focusedNode?.id === requester.id) {
			return true
		}

		// Check if current focus holder allows the change
		if (this.focusedNode?.shouldRelinquishFocus) {
			if (!this.focusedNode.shouldRelinquishFocus(requester)) {
				return false // Current focus denied the request
			}
		}

		// Stop any running transition
		this.focusTransition?.stop()

		const oldFocus = this.focusedNode

		// Release current focus
		if (oldFocus) {
			oldFocus.onFocusLost?.()
		} else if (this.defaultFocus) {
			this.defaultFocus.onFocusLost?.()
		} else {
			this.viewportHandler?.onFocusLost?.()
		}

		// Update focus state
		this.focusedNode = requester

		// Determine the transition config to use
		const nodeTransition = requester.focusTransition?.(this.camera)
		let finalConfig: FocusTransitionConfig | undefined

		if (transition && "target" in transition) {
			// Full config provided - use it entirely
			finalConfig = transition
		} else if (transition) {
			// Just params provided - merge with node's config
			finalConfig = nodeTransition ? {
				target: nodeTransition.target,
				params: { ...nodeTransition.params, ...transition }
			} : undefined
		} else {
			// No override - use node's config
			finalConfig = nodeTransition
		}

		if (finalConfig && this.focusTransition) {
			const params = finalConfig.params ?? {}

			// Handle preserveCurrentState - instant focus change with no camera movement
			if (params.preserveCurrentState) {
				// Immediately grant focus without moving camera
				requester.onFocusGained?.(this.camera)
				this.notifyFocusSettled(requester)
				params.onComplete?.()
			} else {
				// Start camera transition, then call onFocusGained when complete
				const originalOnComplete = params.onComplete

				this.focusTransition.start(finalConfig.target, {
					...params,
					onComplete: () => {
						requester.onFocusGained?.(this.camera!)
						this.notifyFocusSettled(requester)
						originalOnComplete?.()
					}
				})
			}
		} else {
			// No transition - immediately grant focus
			requester.onFocusGained?.(this.camera)
			this.notifyFocusSettled(requester)
		}

		// Notify listeners
		this.notifyFocusChange(requester, oldFocus)

		return true
	}

	/**
	 * Release the focus and return to default focus (if set) or viewport
	 *
	 * @param transition Optional transition config to override the default focus's transition
	 */
	public releaseFocus(transition?: FocusTransitionConfig | FocusTransitionParams): void {
		if (!this.camera) {
			this.context.logger.warn("[FocusManager] Cannot release focus - camera not set")
			return
		}

		if (!this.focusedNode) {
			return // Already at default/viewport
		}

		// Stop any running transition
		this.focusTransition?.stop()

		const oldFocus = this.focusedNode

		// Release current focus
		oldFocus.onFocusLost?.()

		// Update state
		this.focusedNode = undefined

		// Determine the new target (default focus or viewport)
		const newTarget = this.defaultFocus ?? this.viewportHandler

		if (newTarget) {
			// Determine the transition config to use
			const nodeTransition = newTarget.focusTransition?.(this.camera)
			let finalConfig: FocusTransitionConfig | undefined

			if (transition && "target" in transition) {
				// Full config provided - use it entirely
				finalConfig = transition
			} else if (transition) {
				// Just params provided - merge with node's config
				finalConfig = nodeTransition ? {
					target: nodeTransition.target,
					params: { ...nodeTransition.params, ...transition }
				} : undefined
			} else {
				// No override - use node's config
				finalConfig = nodeTransition
			}

			if (finalConfig && this.focusTransition) {
				const params = finalConfig.params ?? {}

				// Handle preserveCurrentState - instant focus change with no camera movement
				if (params.preserveCurrentState) {
					// Immediately grant focus without moving camera
					newTarget.onFocusGained?.(this.camera)
					params.onComplete?.()
				} else {
					// Start camera transition, then call onFocusGained when complete
					const originalOnComplete = params.onComplete

					this.focusTransition.start(finalConfig.target, {
						...params,
						onComplete: () => {
							newTarget.onFocusGained?.(this.camera!)
							originalOnComplete?.()
						}
					})
				}
			} else {
				// No transition - immediately grant focus
				newTarget.onFocusGained?.(this.camera)
			}
		}

		// Notify listeners (newFocus is undefined since we're returning to default/viewport)
		this.notifyFocusChange(undefined, oldFocus)
	}

	// ========== Transition State ==========

	/**
	 * Check if a focus transition is currently running
	 */
	public isTransitioning(): boolean {
		return this.focusTransition?.running ?? false
	}

	/**
	 * Cancel any running focus transition
	 * @param snapToTarget If true, snap camera to the target position
	 */
	public cancelTransition(snapToTarget = false): void {
		this.focusTransition?.cancel(snapToTarget)
	}

	// ========== Focus Change Events ==========

	/**
	 * Subscribe to focus change events
	 * @returns Unsubscribe function
	 */
	public onFocusChange(callback: FocusChangeCallback): () => void {
		this.focusChangeCallbacks.add(callback)
		return () => this.focusChangeCallbacks.delete(callback)
	}

	private notifyFocusChange(newFocus?: FocusTarget, oldFocus?: FocusTarget): void {
		this.focusChangeCallbacks.forEach(cb => cb(newFocus, oldFocus))
	}

	/**
	 * Subscribe to focus settled events: fired for a requestFocus() node once
	 * it has received onFocusGained - after its camera transition completes,
	 * or synchronously (before the focus change notification) when the focus
	 * change involved no camera movement. Not fired when a transition is
	 * superseded by another focus change or release.
	 * @returns Unsubscribe function
	 */
	public onFocusSettled(callback: FocusSettledCallback): () => void {
		this.focusSettledCallbacks.add(callback)
		return () => this.focusSettledCallbacks.delete(callback)
	}

	private notifyFocusSettled(node: FocusTarget): void {
		this.focusSettledCallbacks.forEach(cb => cb(node))
	}

	// ========== Frame Update ==========

	/**
	 * Called every frame for focus-related updates
	 * (e.g., following a focused satellite)
	 *
	 * Note: Does NOT call onFocusUpdate during a transition - the transition
	 * controls the camera during that time.
	 */
	public update(): void {
		// Skip onFocusUpdate during transitions - let the transition control the camera
		if (this.isTransitioning()) {
			return
		}
		this.getActiveHandler()?.onFocusUpdate?.()
	}
}

